import {
	AchReceivedPayment,
	RealtimeReceivedPayment,
	ReceivedPayment,
	ReceivedPaymentTypeFilters,
	ReceivedPaymentTypes,
	WireReceivedPayment,
} from "../../resources/receivedPayments"
import moment from "moment"
import numeral from "numeral"

type AchOrWireReceivedPayment = AchReceivedPayment | WireReceivedPayment
type CommonReceivedPaymentAttribute = keyof AchReceivedPayment["attributes"] & keyof WireReceivedPayment["attributes"]

export function getReceivedPaymentType(receivedPayment: ReceivedPayment) {
	if (receivedPayment.type === ReceivedPaymentTypes.ACH) {
		return ReceivedPaymentTypeFilters.ACH
	}
	if (receivedPayment.type === ReceivedPaymentTypes.RTP) {
		return ReceivedPaymentTypeFilters.RTP
	}
	return ReceivedPaymentTypeFilters.Wire
}

export function getReceivedPaymentReasonCode(receivedPayment: ReceivedPayment) {
	if (isRtpReceivedPayment(receivedPayment)) {
		return receivedPayment.attributes.rejectReason ?? receivedPayment.attributes.returnReason
	}
	if (receivedPayment.type === ReceivedPaymentTypes.ACH) {
		return receivedPayment.attributes.achReturnReason
	}
	return receivedPayment.attributes.wireReturnReason
}

export function getReceivedPaymentReason(receivedPayment: ReceivedPayment) {
	if (isRtpReceivedPayment(receivedPayment)) {
		return undefined
	}
	return receivedPayment.attributes.returnReason
}

export function getReceivedPaymentCommonAttributes<K extends CommonReceivedPaymentAttribute>(
	receivedPayment: ReceivedPayment,
	attribute: K
): AchOrWireReceivedPayment["attributes"][K] | undefined {
	return !isRtpReceivedPayment(receivedPayment) ? receivedPayment.attributes[attribute] : undefined
}

export function isRtpReceivedPayment(receivedPayment: ReceivedPayment): receivedPayment is RealtimeReceivedPayment {
	return receivedPayment.type === ReceivedPaymentTypes.RTP
}

export function extractReceivedPaymentFields(receivedPayment: ReceivedPayment) {
	const isAch = receivedPayment.type === ReceivedPaymentTypes.ACH
	const amount = receivedPayment.attributes.amount && numeral(receivedPayment.attributes.amount / 100).format("$0,0.00")
	const createdAt = receivedPayment.attributes.createdAt && moment(receivedPayment.attributes.createdAt).format("L")
	const estimatedCompletion = !isRtpReceivedPayment(receivedPayment)
		? receivedPayment.attributes.completionDate && moment(receivedPayment.attributes.completionDate).format("L")
		: undefined
	const type = receivedPayment.type
	const direction = receivedPayment.attributes.direction || undefined
	const reasonCode = getReceivedPaymentReasonCode(receivedPayment)
	const reason = getReceivedPaymentReason(receivedPayment)
	const reasonDisplay = reason && reasonCode ? `${reasonCode} - ${reason}` : reason || reasonCode
	const name = isRtpReceivedPayment(receivedPayment)
		? receivedPayment.attributes.counterparty?.name
		: receivedPayment.attributes.companyName
	const routingNumber = isRtpReceivedPayment(receivedPayment)
		? receivedPayment.attributes.counterparty?.routingNumber
		: receivedPayment.attributes.counterpartyRoutingNumber
	const accountNumber = isRtpReceivedPayment(receivedPayment)
		? receivedPayment.attributes.counterparty?.accountNumber
		: undefined
	const originatorEntityId = isRtpReceivedPayment(receivedPayment)
		? undefined
		: receivedPayment.attributes.originatorEntityId

	const receivingEntityName = !isRtpReceivedPayment(receivedPayment)
		? receivedPayment.attributes.receivingEntityName ?? "N/A"
		: undefined

	const addenda = isAch
		? (receivedPayment as ReceivedPayment & {attributes: AchReceivedPayment["attributes"]}).attributes.addenda
		: undefined
	const traceNumber = isAch
		? (receivedPayment as ReceivedPayment & {attributes: AchReceivedPayment["attributes"]}).attributes.traceNumber
		: undefined
	const secCode = isAch
		? (receivedPayment as ReceivedPayment & {attributes: AchReceivedPayment["attributes"]}).attributes.secCode
		: undefined
	const advanced = isAch
		? (receivedPayment as ReceivedPayment & {attributes: AchReceivedPayment["attributes"]}).attributes.wasAdvanced
			? "Yes"
			: "No"
		: undefined

	const rtpReturnReason = receivedPayment.attributes.returnReason
	const rtpRejectedReason = isRtpReceivedPayment(receivedPayment) && receivedPayment.attributes.rejectReason

	const accountId = receivedPayment.relationships.account?.data.id || undefined
	const transactionId = receivedPayment.relationships.receivePaymentTransaction?.data.id || undefined
	const customerData = receivedPayment.relationships.customer?.data
	const customer = customerData?.id != null ? customerData : undefined
	const description = receivedPayment.attributes.description || undefined
	return {
		amount,
		createdAt,
		estimatedCompletion,
		type,
		direction,
		reasonCode,
		reasonDisplay,
		name,
		routingNumber,
		accountNumber,
		originatorEntityId,
		receivingEntityName,
		addenda,
		traceNumber,
		secCode,
		advanced,
		accountId,
		reason,
		transactionId,
		customer,
		description,
		rtpReturnReason,
		rtpRejectedReason,
	}
}
